import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy,
  PLATFORM_ID, ViewChild, ViewEncapsulation
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser, NgStyle } from '@angular/common';

import { interval, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { DeviceService, SdButtonComponent, SizesEnum } from '@seb/web-design-system/core';

import type { IRotation360Config } from './rotation-360.interface';

// eslint-disable-next-line no-var
declare var ThreeSixty: any;

@Component({
  selector: 'sd-rotation-360',
  templateUrl: './rotation-360.component.html',
  styleUrls: ['./rotation-360.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgStyle, SdButtonComponent, TranslateModule]
})
export class SdRotation360Component implements AfterViewInit, OnDestroy {
  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  clicked = false;
  play = false;
  timeoutHandler: Subscription;
  sizesEnum = SizesEnum;
  threeSixty;
  customStyle;
  deviceSubscription: Subscription;
  mobileSubscription: Subscription;
  isDevice$: boolean;
  isMobile$: boolean;

  @Input()
  context = 'rotation-360';

  @Input()
  medias: IRotation360Config;

  @Input()
  autoplay = false;

  @ViewChild('canvasElement')
  canvasElement: ElementRef;

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      if (!this.isThreeSixtyApiLoaded()) {
        this.loadThreeSixtyApi();
      } else {
        this.initThreeSixty();
      }
    }
  }

  ngOnDestroy() {
    this.threeSixty?.destroy();
    this.mobileSubscription?.unsubscribe();
    this.deviceSubscription?.unsubscribe();
  }

  togglePlay() {
    if (this.play) {
      this.threeSixty.stop();
    } else {
      this.threeSixty.play();
    }
    this.play = !this.play;
  }

  mouseup() {
    this.timeoutHandler?.unsubscribe();
    this.clicked = false;
  }

  mousedown() {
    if (this.play === true) {
      this.threeSixty.stop();
    }
    this.play = false;
    this.timeoutHandler = interval(100).subscribe(() => {
      this.clicked = true;
      this.cdRef.markForCheck();
    });
  }

  updateThreeSixty() {
    this.threeSixty?.destroy();
    setTimeout(() => {
      const ratio = this.isMobile$
        ? 240 / this.medias.width
        : this.isDevice$
          ? 500 / this.medias.width
          : 1;

      this.threeSixty = new ThreeSixty(this.canvasElement.nativeElement, {
        image: this.medias.images,
        count: this.medias.count || null,
        perRow: this.medias.perRow || null,
        speed: this.medias.speed || 100,
        width: this.medias.width * ratio || 300,
        height: this.medias.height * ratio || 300,
        prev: this.medias.prev || null,
        next: this.medias.next || null,
        keys: this.medias.keys || true,
        draggable: this.medias.draggable || true,
        swipeable: this.medias.swipeable || true,
        dragTolerance: this.medias.dragTolerance || 10,
        swipeTolerance: this.medias.swipeTolerance || 10,
        swipeTarget: this.medias.swipeTarget,
        inverted: this.medias.inverted || false
      });
      this.customStyle = {
        'width.px': this.medias.width * ratio + 30,
        'height.px': this.medias.height * ratio + 30
      };
    }, 500);
  }

  private isThreeSixtyApiLoaded(): boolean {
    return !!this.document.querySelector(
      'script[src="https://cdn.jsdelivr.net/npm/@mladenilic/threesixty.js/dist/threesixty.js"]',
    );
  }

  private initThreeSixty(): void {
    this.deviceSubscription = this.deviceService.isDevice.subscribe((x) => {
      this.isDevice$ = x;
      this.updateThreeSixty();
      this.cdRef.markForCheck();
    });

    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.updateThreeSixty();
      this.cdRef.markForCheck();
    });

    if (this.autoplay) {
      setTimeout(() => {
        this.threeSixty.play();
        this.play = true;
        this.clicked = false;
      }, 500); // time to init threeSixty (see updateThreeSixty)
    }
  }

  private loadThreeSixtyApi() {
    const script: HTMLScriptElement = this.document.createElement('script');
    script.src =
      'https://cdn.jsdelivr.net/npm/@mladenilic/threesixty.js/dist/threesixty.js';
    script.integrity = 'sha384-tOcZLyyZ5tQzoCzrJumYosaKq/ZdRqn5rFklr46Vop/XarMluziGunja1voWHoNg';
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.defer = true;
    script.onload = () => this.initThreeSixty();
    this.document.body.appendChild(script);
  }
}
